import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { DocumentActions } from "@/pages/legal/Agreements";
import { StagedImages, uploadStaged } from "@/pages/legal/LegalImages";

const STATUSES = ["OPEN", "HEARING_SCHEDULED", "IN_PROGRESS", "CLOSED", "SETTLED", "DISMISSED"];

export default function CasesAndNotices() {
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: cases }, { data: projectList }] = await Promise.all([api.get("/legal/cases"), api.get("/projects")]);
      setRows(cases);
      setProjects(projectList);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="cases-and-notices-page">
      <PageHeader title="Cases & Notices" description="Case number, forum, counsel and next hearing date for every legal matter." action={<CreateCaseDialog projects={projects} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && rows.length === 0 ? (
          <EmptyState title="No cases yet" testId="cases-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Case No.</TableHead><TableHead>Forum</TableHead><TableHead>Party</TableHead><TableHead>Counsel</TableHead><TableHead>Next Hearing</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`case-row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-foreground">{r.case_number}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.forum}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.party}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.counsel || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(r.next_hearing_date)}</TableCell>
                  <TableCell><StatusUpdateSelect caseRow={r} onChanged={load} /></TableCell>
                  <TableCell className="text-right"><DocumentActions entity={r} kind="cases" onUploaded={load} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function StatusUpdateSelect({ caseRow, onChanged }) {
  const update = async (status) => {
    try {
      await api.patch(`/legal/cases/${caseRow.id}`, { status });
      toast.success("Status updated");
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  return (
    <Select value={caseRow.status} onValueChange={update}>
      <SelectTrigger className="h-7 w-40" data-testid={`case-status-select-${caseRow.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function CreateCaseDialog({ projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", case_number: "", forum: "", party: "", filed_date: "", next_hearing_date: "", counsel: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  useResetOnClose(Boolean(open), () => { setForm({ project_id: "", case_number: "", forum: "", party: "", filed_date: "", next_hearing_date: "", counsel: "", remarks: "" }); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: made } = await api.post("/legal/cases", { ...form, project_id: form.project_id || null, filed_date: form.filed_date || null, next_hearing_date: form.next_hearing_date || null });
      toast.success("Case created");
      await uploadStaged("cases", made.id, images);
      setImages([]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-case-button"><Plus className="h-4 w-4" /> New Case</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Case / Notice</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-case-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Case number</Label><Input required value={form.case_number} onChange={set("case_number")} className="mt-1.5" data-testid="case-number-input" /></div>
            <div><Label>Forum</Label><Input required value={form.forum} onChange={set("forum")} className="mt-1.5" data-testid="case-forum-input" /></div>
          </div>
          <div><Label>Party</Label><Input required value={form.party} onChange={set("party")} className="mt-1.5" data-testid="case-party-input" /></div>
          <div>
            <Label>Project (optional)</Label>
            <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="case-project-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">Not linked</SelectItem>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Filed date</Label><Input type="date" value={form.filed_date} onChange={set("filed_date")} className="mt-1.5" data-testid="case-filed-date-input" /></div>
            <div><Label>Next hearing</Label><Input type="date" value={form.next_hearing_date} onChange={set("next_hearing_date")} className="mt-1.5" data-testid="case-next-hearing-input" /></div>
          </div>
          <div><Label>Counsel</Label><Input value={form.counsel} onChange={set("counsel")} className="mt-1.5" data-testid="case-counsel-input" /></div>
          <div><Label>Remarks</Label><Textarea value={form.remarks} onChange={set("remarks")} className="mt-1.5" data-testid="case-remarks-input" /></div>
          <StagedImages files={images} onChange={setImages} />
          <DialogFooter><Button type="submit" disabled={saving} data-testid="create-case-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
